import React from 'react';
import { Link } from 'react-router-dom';
import AdSlot from './AdSlotComponent';
import Card from './ui/Card';

/**
 * Artigo editorial longo — reforça “conteúdo de valor” para revisão AdSense / SEO.
 */
function ArtigoRefinamentoBacklog() {
    return (
        <Card className="p-6 max-w-3xl mx-auto text-left" data-editorial-content="true">
            <article>
                <h1 className="text-2xl font-bold mb-4">
                    Refinamento de backlog e estimativas: papel do Planning Poker no Scrum
                </h1>
                <p className="text-slate-500 text-sm mb-6">
                    Publicado no Pleini Pouquer · conteúdo editorial em português para equipes ágeis
                </p>

                <h2 className="text-xl font-semibold mt-6 mb-2">Introdução</h2>
                <p className="mb-3 text-slate-300 leading-relaxed">
                    O refinamento de backlog (ou &quot;backlog refinement&quot;, antigamente grooming) é o processo
                    contínuo de deixar as histórias de usuário suficientemente claras, pequenas e priorizadas
                    para que o time possa comprometer-se com elas no planejamento de sprint. Sem refinamento,
                    o Planning Poker vira um jogo de adivinhação: não há base comum para discutir esforço ou
                    complexidade. Este artigo explica como conectar refinamento e estimativa, onde o Planning
                    Poker se encaixa, e como evitar armadilhas que geram números inúteis ou discussões
                    intermináveis.
                </p>

                <h2 className="text-xl font-semibold mt-6 mb-2">O que o refinamento precisa entregar</h2>
                <p className="mb-3 text-slate-300 leading-relaxed">
                    Uma história pronta para estimativa costuma ter: descrição compreensível para o time
                    técnico, critérios de aceite testáveis, dependências identificadas e, quando possível,
                    mockups ou exemplos. O Product Owner não precisa resolver tudo sozinho: o refinamento é
                    colaborativo — desenvolvedores, QA e design podem questionar ambiguidades. Quanto mais
                    dúvidas forem sanadas antes da sessão de Planning Poker, mais rápida será a convergência
                    dos votos. Se a história ainda estiver vaga, o time deve preferir quebrá-la, esclarecer
                    escopo com o negócio ou adiar a estimativa em vez de forçar um número falso.
                </p>

                <h2 className="text-xl font-semibold mt-6 mb-2">Planning Poker após (ou durante) o refinamento</h2>
                <p className="mb-3 text-slate-300 leading-relaxed">
                    Em muitos times, o refinamento semanal produz uma lista de histórias &quot;prontas para
                    estimar&quot;. Na mesma reunião ou em uma sessão dedicada, aplica-se o Planning Poker para
                    atribuir story points ou outra unidade relativa. A técnica funciona melhor quando todos
                    os que vão implementar ou testar participam da votação; abstentions ou &quot;só observar&quot;
                    enfraquecem o compromisso. Ferramentas online como o Pleini Pouquer ajudam times remotos a
                    manter o ritual de revelação simultânea e o registro de histórias na ordem correta.
                </p>

                <h2 className="text-xl font-semibold mt-6 mb-2">Story points: relativos, não horas</h2>
                <p className="mb-3 text-slate-300 leading-relaxed">
                    Story points medem esforço relativo e incerteza, não horas exatas. O time calibra a escala
                    com histórias já concluídas (&quot;esta parece o dobro daquela&quot;). Divergências grandes
                    entre votos são oportunidade de aprendizado: alguém viu risco técnico que outros não
                    viram, ou interpretou o escopo de forma diferente. O facilitador deve encorajar conversa
                    curta e nova rodada de votos, evitando pressão por consenso artificial.
                </p>

                <h2 className="text-xl font-semibold mt-6 mb-2">Erros que geram &quot;conteúdo vazio&quot; nas estimativas</h2>
                <ul className="list-disc list-inside mb-3 text-slate-300 space-y-2 leading-relaxed">
                    <li>
                        Estimar histórias gigantes em vez de fatiá-las — o erro aparece tarde no sprint.
                    </li>
                    <li>
                        Deixar só o líder técnico votar — perde-se a diversidade de perspectivas.
                    </li>
                    <li>
                        Confundir velocidade do time com produtividade individual — métricas devem melhorar o
                        fluxo, não punir pessoas.
                    </li>
                    <li>
                        Ignorar débito técnico explícito nas discussões — o ponto reflete trabalho real,
                        incluindo testes e refatoração necessária.
                    </li>
                </ul>

                <h2 className="text-xl font-semibold mt-6 mb-2">Conclusão</h2>
                <p className="mb-3 text-slate-300 leading-relaxed">
                    Refinamento sólido e Planning Poker disciplinado se reforçam mutuamente: o primeiro reduz
                    ruído; o segundo transforma consenso em números comparáveis no backlog. Se você está
                    introduzindo a prática no time, combine leitura do{' '}
                    <Link to="/guia" className="text-sky-400 hover:underline">
                        guia de Planning Poker
                    </Link>{' '}
                    com esta visão de fluxo Scrum. Para começar a votar hoje,{' '}
                    <Link to="/create" className="text-sky-400 hover:underline">
                        crie uma sala
                    </Link>{' '}
                    no Pleini Pouquer e convide o time pelo link.
                </p>

                <p className="text-slate-500 text-sm mt-8">
                    Texto original · última revisão: abril de 2026
                </p>
            </article>

            <AdSlot adSlot="7348901130475950" format="auto" />
        </Card>
    );
}

export default ArtigoRefinamentoBacklog;
